import React, { useState, useEffect } from 'react';
import ToastConfig from './utils/ToastConfig';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Training from './components/Training';
import Testing from './components/Testing';
import Login from './components/auth/Login';
import ChatbotEmbed from './components/ChatbotEmbed';
import ChatbotDemo from './components/ChatbotDemo';
import ForgotPassword from './components/auth/ForgotPassword';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import { checkAuthStatus } from './api';
import ChatbotWidget from './components/ChatbotWidget';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ChatbotPreview from './components/ChatbotPreview';
import ChatbotIframe from './components/ChatbotIframe';

// Import themes
import { themes } from './themes';

// Conditionally import payment components only if not in chatbot iframe mode
const isChatbotMode = window.location.pathname.includes('/chatbot-') || window.location.pathname.includes('/embed');
let PricingPage, PaymentSuccessPage, PaymentCancelPage, CustomBilling;

if (!isChatbotMode) {
  // Only import payment components when not in chatbot mode to avoid Stripe errors
  PricingPage = React.lazy(() => import('./components/payments/PricingPage'));
  PaymentSuccessPage = React.lazy(() => import('./components/payments/PaymentSuccessPage'));
  PaymentCancelPage = React.lazy(() => import('./components/payments/PaymentCancelPage'));
  CustomBilling = React.lazy(() => import('./components/payments/CustomBilling'));
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await checkAuthStatus();
        setIsAuthenticated(response.status === 'success');
        if (response.status === 'success') {
          setUserData(response.data);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? React.cloneElement(children, { userData }) : <Navigate to="/login" replace />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="training" element={<Training />} />
          <Route path="testing" element={<Testing />} />
          <Route path="settings" element={<ChatbotDemo />} />
        </Route>
        <Route path="/embed" element={
          <EmbedChatbot />
        } />
        <Route path="/chatbot-widget" element={
          <EmbedChatbot />
        } />
        <Route path="/chatbot-button" element={
          <EmbedChatbot />
        } />
        {/* Only include payment routes when not in chatbot mode */}
        {!isChatbotMode && CustomBilling && (
          <Route path="/custom-billing" element={
            <ProtectedRoute>
              <React.Suspense fallback={<div>Loading...</div>}>
                <CustomBilling />
              </React.Suspense>
            </ProtectedRoute>
          } />
        )}
        {!isChatbotMode && PaymentSuccessPage && (
          <Route path="/payment-success" element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <PaymentSuccessPage />
            </React.Suspense>
          } />
        )}
        {!isChatbotMode && PaymentCancelPage && (
          <Route path="/payment-cancel" element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <PaymentCancelPage />
            </React.Suspense>
          } />
        )}
      </Routes>
      <ToastContainer />
    </Router>
  );
}

// Embed component that handles URL parameters
function EmbedChatbot() {
  const params = new URLSearchParams(window.location.search);
  
  // Check if this is for button-only mode (small iframe)
  const isButtonOnly = window.location.pathname === '/chatbot-button';
  const [isOpen, setIsOpen] = useState(isButtonOnly ? false : (params.get('isOpen') === 'true'));
  
  // Load button-only CSS if needed
  React.useEffect(() => {
    if (isButtonOnly) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/src/components/ButtonOnly.css';
      document.head.appendChild(link);
      
      // Also add inline styles to ensure proper sizing
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'visible';
      document.body.style.background = 'transparent';
      document.body.style.width = '120px';
      document.body.style.height = '120px';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
      document.documentElement.style.overflow = 'visible';
      document.documentElement.style.width = '120px';
      document.documentElement.style.height = '120px';
    }
  }, [isButtonOnly]);
  
  // Get theme configuration from URL parameters
  const themeId = params.get('theme') || 'professional';
  const theme = themes[themeId];
  
  // Override theme config with URL parameters
  if (theme) {
    theme.config = {
      ...theme.config,
      color: params.get('color') || theme.config.color,
      typography: params.get('typography') || theme.config.typography,
      welcomeMessage: params.get('welcomeMessage') || theme.config.welcomeMessage,
      name: params.get('title') || theme.config.name,
      description: params.get('subtitle') || theme.config.description,
      chatbotId: params.get('chatbotId') || theme.config.chatbotId
    };
  }

  // Custom setIsOpen for button-only mode
  const handleSetIsOpen = (newIsOpen) => {
    if (isButtonOnly && newIsOpen) {
      // If this is button-only mode and trying to open, send message to parent
      window.parent.postMessage({
        type: 'openChat',
        config: {
          theme: themeId,
          iconUrl: params.get('iconUrl'),
          name: params.get('title'),
          chatbotId: params.get('chatbotId'),
          userId: params.get('userId'),
          welcomeMessage: params.get('welcomeMessage'),
          description: params.get('subtitle'),
          color: params.get('color')
        }
      }, '*');
    } else {
      setIsOpen(newIsOpen);
    }
  };

  return (
    <ChatbotPreview
      theme={theme || themes.professional}
      iconUrl={params.get('iconUrl') || null}
      isOpen={isOpen}
      setIsOpen={handleSetIsOpen}
      userId={params.get('userId')}
      chatbotId={params.get('chatbotId')}
    />
  );
}

export default App;